import { sortBy } from 'lodash';

import {
  buildOrgWeightPathKey,
  orgWeightPathSegment,
} from 'js/utils/org-utils';
import { applyTrendWindow, normalizeTeamLineId } from 'js/utils/org-trends';
import { rollupOrgChartScore } from 'js/utils/org-assessment-rollup';

const DAY_MS = 24 * 60 * 60 * 1000;

function pathSegment(node, type, pathKey) {
  return {
    pathKey,
    depthId: pathKey,
    isRoot: type === 'org' && Boolean(node.is_root),
    name: node.name,
    id: node.id,
    type,
  };
}

const HOUR_MS = 60 * 60 * 1000;
const DEBOUNCE_STEPS_MS = [HOUR_MS, DAY_MS, 7 * DAY_MS, 30 * DAY_MS];

/**
 * How close together answers must be to count as one point: the longest of
 * an hour, a day, a week or a month (30 days) that is no more than half the
 * median gap between a team's own consecutive answers. Teams that assess
 * quarterly get a month to finish a round; teams that assess weekly get a
 * day; teams that answer several times a day get an hour. A day when no team
 * has answered twice. Mirrors OrgEvidence.debounce_seconds/1 so briefing
 * charts and org trend charts plot the same points.
 */
export function trendDebounceWindowMs(seriesList = []) {
  const gaps = [];

  for (const series of seriesList) {
    const times = [
      ...new Set(
        (series ?? [])
          .filter((p) => p?.date && p.score != null)
          .map((p) => p.date.getTime())
      ),
    ].sort((a, b) => a - b);

    for (let i = 1; i < times.length; i += 1) {
      gaps.push(times[i] - times[i - 1]);
    }
  }

  if (!gaps.length) {
    return DAY_MS;
  }

  gaps.sort((a, b) => a - b);
  const half = gaps[Math.floor(gaps.length / 2)] / 2;
  const fitting = DEBOUNCE_STEPS_MS.filter((step) => step <= half);

  return fitting.length ? fitting[fitting.length - 1] : DEBOUNCE_STEPS_MS[0];
}

/**
 * At most one point per window: the first point opens the window; the
 * emitted point uses the last score/time within that window.
 */
export function debounceTrendPoints(points = [], windowMs = DAY_MS) {
  const sorted = [...points]
    .filter((p) => p?.date && p.score != null)
    .sort((a, b) => a.date - b.date);

  if (!sorted.length) {
    return [];
  }

  const debounced = [];
  let bucket = [sorted[0]];

  for (let i = 1; i < sorted.length; i += 1) {
    const point = sorted[i];
    const windowStart = bucket[0].date.getTime();

    if (point.date.getTime() - windowStart < windowMs) {
      bucket.push(point);
    } else {
      const last = bucket[bucket.length - 1];
      debounced.push({ date: last.date, score: last.score });
      bucket = [point];
    }
  }

  const last = bucket[bucket.length - 1];
  debounced.push({ date: last.date, score: last.score });

  return debounced;
}

/** Latest known score at or before a timestamp (accumulated state). */
export function scoreAtOrBefore(series, timeMs) {
  if (!series?.length) {
    return null;
  }

  let lastScore = null;

  for (const point of series) {
    if (point.date.getTime() > timeMs) {
      break;
    }

    if (point.score != null) {
      lastScore = point.score;
    }
  }

  return lastScore;
}

function uniqueTrendDates(points = []) {
  const times = new Set();
  const dates = [];

  [...points]
    .filter((p) => p?.date && p.score != null)
    .sort((a, b) => a.date - b.date)
    .forEach((p) => {
      const time = p.date.getTime();
      if (!times.has(time)) {
        times.add(time);
        dates.push(p.date);
      }
    });

  return dates;
}

function trendTimelineForOrg(
  orgNode,
  teamSeriesById,
  debouncePoints = true,
  windowMs = DAY_MS
) {
  const points = [];

  collectDescendantTeamIds(orgNode).forEach((teamId) => {
    points.push(...(teamSeriesById.get(teamId) ?? []));
  });

  if (debouncePoints === false) {
    return uniqueTrendDates(points);
  }

  return debounceTrendPoints(points, windowMs).map((point) => point.date);
}

function collectDescendantTeamIds(orgNode, ids = new Set()) {
  for (const team of orgNode.teams ?? []) {
    ids.add(normalizeTeamLineId(team.id));
  }

  for (const childOrg of orgNode.child_orgs ?? []) {
    collectDescendantTeamIds(childOrg, ids);
  }

  return ids;
}

function orgTierPaletteClass(depth) {
  return `palette-chart-tiers-light-${1 + (depth % 9)}`;
}

function collectAllOrgNodes(organization) {
  const nodes = [];

  const walk = (orgNode, pathToNode, depth, parentPathKey) => {
    nodes.push({
      orgNode,
      pathToNode,
      parentPathKey,
      depth,
      id: orgNode.id,
      name: orgNode.name,
    });

    const { rowKey, childPath } = childPathForOrg(
      orgNode,
      pathToNode,
      parentPathKey
    );

    for (const childOrg of sortBy(orgNode.child_orgs ?? [], (o) =>
      o.name?.toUpperCase()
    )) {
      walk(childOrg, childPath, depth + 1, rowKey);
    }
  };

  walk(organization, [], 0, '');

  return nodes;
}

function collectOrgIdsInSubtree(orgNode, ids = new Set()) {
  ids.add(orgNode.id);

  for (const childOrg of orgNode.child_orgs ?? []) {
    collectOrgIdsInSubtree(childOrg, ids);
  }

  return ids;
}

export function visibleTrendLineScope(selectedPickerRow) {
  if (!selectedPickerRow) {
    return null;
  }

  if (selectedPickerRow.type === 'team') {
    const teamId = normalizeTeamLineId(
      selectedPickerRow.id ?? selectedPickerRow.node?.id
    );

    return {
      orgIds: new Set(),
      teamIds: new Set(teamId != null ? [teamId] : []),
    };
  }

  const orgNode = selectedPickerRow.node;

  if (!orgNode) {
    return null;
  }

  return {
    orgIds: collectOrgIdsInSubtree(orgNode),
    teamIds: collectDescendantTeamIds(orgNode),
  };
}

export function filterTrendLinesForPickerScope(lines, selectedPickerRow) {
  const scope = visibleTrendLineScope(selectedPickerRow);

  if (!scope) {
    return lines;
  }

  return lines.filter((line) => {
    if (line.isRollup) {
      const orgId = Number(String(line.id).replace(/^org-/, ''));

      return scope.orgIds.has(orgId);
    }

    return scope.teamIds.has(normalizeTeamLineId(line.id));
  });
}

function childPathForOrg(orgNode, pathToNode, parentPathKey) {
  const rowKey = buildOrgWeightPathKey(
    parentPathKey,
    orgWeightPathSegment('org', orgNode.id)
  );

  return {
    rowKey,
    childPath: [...pathToNode, pathSegment(orgNode, 'org', rowKey)],
  };
}

function rollupOrgNodeAtTime(
  orgNode,
  pathToNode,
  parentPathKey,
  timeMs,
  teamSeriesById,
  orgSeriesById,
  weightContext
) {
  const { childPath } = childPathForOrg(orgNode, pathToNode, parentPathKey);

  const childItems = [
    ...sortBy(orgNode.teams ?? [], (team) => team.name?.toUpperCase()).map(
      (team) => ({
        type: 'team',
        id: team.id,
        name: team.name,
        teams: team.teams,
        score: scoreAtOrBefore(
          teamSeriesById.get(normalizeTeamLineId(team.id)),
          timeMs
        ),
      })
    ),
    ...sortBy(orgNode.child_orgs ?? [], (org) => org.name?.toUpperCase()).map(
      (childOrg) => ({
        type: 'org',
        id: childOrg.id,
        name: childOrg.name,
        child_orgs: childOrg.child_orgs,
        teams: childOrg.teams,
        score: scoreAtOrBefore(orgSeriesById.get(childOrg.id), timeMs),
      })
    ),
  ];

  return rollupOrgChartScore(childItems, weightContext, childPath);
}

export function buildOrgRollupTrendLines({
  organization,
  teams = [],
  weightContext,
  getTeamSeries,
  window,
  debounceTrendPoints = true,
}) {
  if (!organization || !teams.length || !getTeamSeries) {
    return [];
  }

  const teamSeriesById = new Map();

  teams.forEach((team) => {
    const teamId = normalizeTeamLineId(team.teamId);
    const series = [...(getTeamSeries(team.series) ?? [])].sort(
      (a, b) => a.date - b.date
    );
    teamSeriesById.set(teamId, series);
  });

  const windowMs = trendDebounceWindowMs([...teamSeriesById.values()]);
  const orgNodes = collectAllOrgNodes(organization);
  const orgSeriesById = new Map();

  const sortedNodes = [...orgNodes].sort((a, b) => b.depth - a.depth);

  sortedNodes.forEach(({ orgNode, pathToNode, parentPathKey, id }) => {
    const timeline = trendTimelineForOrg(
      orgNode,
      teamSeriesById,
      debounceTrendPoints,
      windowMs
    );

    if (!timeline.length) {
      return;
    }

    const points = [];

    timeline.forEach((date) => {
      const score = rollupOrgNodeAtTime(
        orgNode,
        pathToNode,
        parentPathKey,
        date.getTime(),
        teamSeriesById,
        orgSeriesById,
        weightContext
      );

      if (score != null) {
        points.push({ date, score });
      }
    });

    if (points.length) {
      orgSeriesById.set(id, points);
    }
  });

  if (!orgSeriesById.size) {
    return [];
  }

  return sortedNodes
    .map(({ depth, id, name }) => {
      const rawData = orgSeriesById.get(id);

      if (!rawData?.length) {
        return null;
      }

      const data = window ? applyTrendWindow(rawData, window) : rawData;

      if (!data.length) {
        return null;
      }

      return {
        id: `org-${id}`,
        label: name,
        depth,
        isRollup: true,
        paletteClass: orgTierPaletteClass(depth),
        data,
      };
    })
    .filter(Boolean);
}

export function orgTrendLineKindLabel({ isRollup, depth = 0 }) {
  if (isRollup) {
    return `Tier ${depth + 1} Group`;
  }

  return 'Team';
}

/** Matches SquadScorecard tier palette classes for line kind labels. */
export function orgTrendLineKindPaletteClass({ isRollup, depth = 0 }) {
  return `palette-tiers-${isRollup ? 1 + (depth % 7) : 0}`;
}

export function mergeTeamAndRollupTrendLines(teamLines = [], rollupLines = []) {
  const rollupsByDepth = [...rollupLines].sort((a, b) => b.depth - a.depth);
  return [...teamLines, ...rollupsByDepth];
}

export function buildOrgTrendsChartLines({
  organization,
  teams,
  weightContext,
  selectedPickerRow,
  getTeamSeries,
  getTrendSeriesForChart,
  window,
  debounceTrendPoints = true,
}) {
  const teamLines = getTrendSeriesForChart(teams, getTeamSeries).map(
    (line) => ({
      ...line,
      isRollup: false,
    })
  );

  const rollupLines = buildOrgRollupTrendLines({
    organization,
    teams,
    weightContext,
    getTeamSeries,
    window,
    debounceTrendPoints,
  });

  return filterTrendLinesForPickerScope(
    mergeTeamAndRollupTrendLines(teamLines, rollupLines),
    selectedPickerRow
  );
}
